import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form } from "formik";
import toastUtils from "../../utils/toastUtils";
import * as Yup from "yup";

import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Tabs,
  Tab,
  Box,
  Paper,
  Typography,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: theme.palette.primary.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  colorPicker: {
    width: "100%",
    height: 40,
    border: "1px solid #ccc",
    borderRadius: 4,
    cursor: "pointer",
  },
  previewContainer: {
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#f5f5f5',
    minHeight: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  widgetPreview: {
    position: "relative",
    width: 300,
    height: 400,
    backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : "white",
    borderRadius: 12,
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  widgetHeader: {
    padding: theme.spacing(2),
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  widgetLogo: {
    height: 24,
    width: "auto",
    marginLeft: "auto",
  },
  widgetBody: {
    flex: 1,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "#666",
    overflow: "auto",
  },
  widgetInputArea: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderTop: "1px solid #e0e0e0",
    display: "flex",
    alignItems: "center",
  },
  widgetInput: {
    flex: 1,
    border: "1px solid #ddd",
    borderRadius: 20,
    padding: "8px 12px",
    fontSize: "0.875rem",
  },
  widgetSendButton: {
    marginLeft: theme.spacing(1),
    backgroundColor: "#2563eb",
    color: "white",
    borderRadius: "50%",
    width: 32,
    height: 32,
    minWidth: 32,
    padding: 0,
  },
  widgetFooter: {
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : "#f0f0f0",
    borderTop: "1px solid #e0e0e0",
    fontSize: "0.75rem",
    color: "#666",
    textAlign: "center",
  },
  codeContainer: {
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : "#f4f4f4",
    padding: theme.spacing(2),
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: "0.875rem",
    wordBreak: "break-all",
  },
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`webchat-tabpanel-${index}`}
      aria-labelledby={`webchat-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const WebChatSettings = ({ companyId }) => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const [queues, setQueues] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [config, setConfig] = useState({
    enabled: true,
    widgetColor: "#2563eb",
    widgetPosition: "right",
    welcomeMessage: i18n.t("settings.webchat.placeholders.welcomeMessage"),
    messagePlaceholder: i18n.t("settings.webchat.placeholders.messagePlaceholder"),
    widgetTitle: i18n.t("settings.webchat.placeholders.widgetTitle"),
    widgetDescription: i18n.t("settings.webchat.placeholders.widgetDescription"),
    customCSS: "",
    autoAssign: true,
    queueId: "",
    respectBusinessHours: true,
    maxConcurrentChats: 5,
    offlineMessage: i18n.t("settings.webchat.placeholders.offlineMessage"),
    showAgentInfo: true,
    allowFileUpload: true,
    allowedFileTypes: "jpg,jpeg,png,pdf,doc,docx",
    maxFileSize: 10,
    requireName: true,
    requireEmail: false,
    requirePhone: false,
    enableAssistant: false,
    assistantId: null,
    companyName: "Sua Empresa",
    companyUrl: "www.suaempresa.com.br",
    companyLogoUrl: null,
  });

  const WebChatSchema = Yup.object().shape({
    widgetColor: Yup.string()
      .matches(/^#[0-9A-F]{6}$/i, i18n.t("settings.webchat.validation.colorFormat"))
      .required(i18n.t("settings.webchat.validation.required")),
    widgetTitle: Yup.string().required(i18n.t("settings.webchat.validation.required")).max(50, i18n.t("settings.webchat.validation.maxLength", { max: 50 })),
    welcomeMessage: Yup.string().required(i18n.t("settings.webchat.validation.required")).max(500, i18n.t("settings.webchat.validation.maxLength", { max: 500 })),
    maxConcurrentChats: Yup.number().positive(i18n.t("settings.webchat.validation.positive")).max(50, i18n.t("settings.webchat.validation.maxValue", { max: 50 })),
    maxFileSize: Yup.number().positive(i18n.t("settings.webchat.validation.positive")).max(100, i18n.t("settings.webchat.validation.maxValue", { max: 100 })),
    companyName: Yup.string().max(50, i18n.t("settings.webchat.validation.maxLength", { max: 50 })),
    companyUrl: Yup.string().max(100, i18n.t("settings.webchat.validation.maxLength", { max: 100 })),
  });

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/webchat/config");
      // Ensure customCSS is never null and queueId/assistantId are properly handled
      setConfig({
        ...data,
        customCSS: data.customCSS || "",
        queueId: data.queueId || "",
        assistantId: data.assistantId || null,
        enableAssistant: data.enableAssistant || false,
        companyName: data.companyName || "Sua Empresa",
        companyUrl: data.companyUrl || "www.suaempresa.com.br",
        companyLogoUrl: data.companyLogoUrl || null,
      });
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchQueues = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      setQueues(data);
    } catch (err) {
      toastError(err);
    }
  }, []);

  const fetchAssistants = useCallback(async () => {
    try {
      setLoadingAssistants(true);
      const { data } = await api.get("/assistants", {
        params: {
          pageNumber: 1,
          pageSize: 100,
        }
      });
      setAssistants(data.assistants || []);
    } catch (err) {
      console.error("Erro ao carregar assistentes:", err);
      toastError(err);
    } finally {
      setLoadingAssistants(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchQueues();
    fetchAssistants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveConfig = async (values, actions) => {
    try {
      // Tratar queueId e assistantId: converter string vazia para null
      const processedValues = {
        ...values,
        queueId: values.queueId === "" || values.queueId === null ? null : parseInt(values.queueId),
        maxConcurrentChats: parseInt(values.maxConcurrentChats),
        maxFileSize: parseInt(values.maxFileSize),
        assistantId: values.assistantId === "" || values.assistantId === null ? null : values.assistantId, // UUID não precisa parseInt
        enableAssistant: Boolean(values.enableAssistant),
      };

      await api.put("/webchat/config", processedValues);
      toastUtils.success(i18n.t("settings.webchat.messages.success"), "Chat Configurado");
      actions.setSubmitting(false);
    } catch (err) {
      toastError(err);
      actions.setSubmitting(false);
    }
  };

  const generateEmbedCode = useCallback(() => {
    // ✅ CORREÇÃO: Usar URLs públicas em produção
    // Prioridade:
    //   1. REACT_APP_BACKEND_URL / REACT_APP_FRONTEND_URL (variáveis de build)
    //   2. window.location.origin (URL atual do CRM)
    //   3. Fallback localhost
    //
    // ⚠️ IMPORTANTE: As URLs precisam ser PÚBLICAS (HTTPS em produção) para
    // funcionarem em sites de clientes (WordPress, etc.). Localhost só
    // funciona se o cliente estiver testando na mesma máquina do servidor.
    const currentOrigin = window.location.origin;

    // FRONTEND (serve o webchat-widget.js):
    //   1º REACT_APP_FRONTEND_URL  →  2º origem atual do CRM
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || currentOrigin;

    // BACKEND (API que recebe as conversas):
    //   1º REACT_APP_BACKEND_URL (embutido no build de produção)
    //   2º só como fallback de DEV (localhost:3000 → localhost:8080)
    //   3º origem atual (caso CRM e API estejam no mesmo domínio via nginx)
    const backendUrl =
      process.env.REACT_APP_BACKEND_URL ||
      (currentOrigin.includes(":3000")
        ? currentOrigin.replace(":3000", ":8080")
        : currentOrigin);

    // Aviso se ainda estiver com localhost (para o admin saber que precisa configurar URL pública)
    const isLocalhost = backendUrl.includes("localhost") || frontendUrl.includes("localhost");
    const warningComment = isLocalhost
      ? `<!-- ⚠️ ATENÇÃO: URLs apontam para "localhost". Este código só funciona na máquina de desenvolvimento. Em produção, faça o build com REACT_APP_BACKEND_URL e REACT_APP_FRONTEND_URL definidos no .env.production, ou substitua as URLs abaixo pelas URLs públicas (HTTPS) do servidor. -->\n`
      : "";

    return `${warningComment}<!-- WebChat Widget -->
<script 
  src="${frontendUrl}/webchat-widget.js" 
  data-company-id="${companyId}"
  data-api-url="${backendUrl}"
  async>
</script>`;
  }, [companyId]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  return (
    <div className={classes.root}>
      <Formik
        initialValues={config}
        enableReinitialize
        validationSchema={WebChatSchema}
        onSubmit={handleSaveConfig}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
              <Tab label={i18n.t("settings.webchat.tabs.appearance")} />
              <Tab label={i18n.t("settings.webchat.tabs.behavior")} />
              <Tab label={i18n.t("settings.webchat.tabs.files")} />
              <Tab label={i18n.t("settings.webchat.tabs.customization")} />
              <Tab label={i18n.t("settings.webchat.tabs.code")} />
            </Tabs>

            <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper className={classes.previewContainer}>
                    <div className={classes.widgetPreview}>
                      <div
                        className={classes.widgetHeader}
                        style={{ backgroundColor: values.widgetColor }}
                      >
                        <div>
                          <Typography variant="subtitle2" style={{ color: "white" }}>
                            {values.widgetTitle}
                          </Typography>
                          <Typography variant="caption" style={{ color: "rgba(255,255,255,0.8)" }}>
                            {values.widgetDescription}
                          </Typography>
                        </div>
                        {values.companyLogoUrl && (
                          <img 
                            src={values.companyLogoUrl} 
                            alt="Logo da Empresa" 
                            className={classes.widgetLogo}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                      <div className={classes.widgetBody}>
                        <Typography variant="body2" style={{ textAlign: "center", marginBottom: 16 }}>
                          {values.welcomeMessage}
                        </Typography>
                      </div>
                      <div className={classes.widgetInputArea}>
                        <div className={classes.widgetInput}>
                          {values.messagePlaceholder}
                        </div>
                        <Button className={classes.widgetSendButton} style={{ backgroundColor: values.widgetColor }}>➤</Button>
                      </div>
                      <div className={classes.widgetFooter}>
                        <div><strong>{values.companyName}</strong></div>
                        <div>{values.companyUrl}</div>
                      </div>
                    </div>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values.enabled}
                            onChange={(e) => setFieldValue("enabled", e.target.checked)}
                            name="enabled"
                            color="primary"
                          />
                        }
                        label={i18n.t("settings.webchat.form.enabled")}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={i18n.t("settings.webchat.form.widgetColor")}
                        name="widgetColor"
                        type="color"
                        value={values.widgetColor}
                        onChange={(e) => setFieldValue("widgetColor", e.target.value)}
                        inputProps={{
                          className: classes.colorPicker,
                          style: { padding: 0, border: 'none' }
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>{i18n.t("settings.webchat.form.widgetPosition")}</InputLabel>
                        <Select
                          value={values.widgetPosition}
                          onChange={(e) => setFieldValue("widgetPosition", e.target.value)}
                        >
                          <MenuItem value="right">{i18n.t("settings.webchat.positions.right")}</MenuItem>
                          <MenuItem value="left">{i18n.t("settings.webchat.positions.left")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={i18n.t("settings.webchat.form.widgetTitle")}
                        name="widgetTitle"
                        value={values.widgetTitle}
                        onChange={(e) => setFieldValue("widgetTitle", e.target.value)}
                        error={touched.widgetTitle && Boolean(errors.widgetTitle)}
                        helperText={touched.widgetTitle && errors.widgetTitle}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={i18n.t("settings.webchat.form.widgetDescription")}
                        name="widgetDescription"
                        value={values.widgetDescription}
                        onChange={(e) => setFieldValue("widgetDescription", e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={i18n.t("settings.webchat.form.welcomeMessage")}
                        name="welcomeMessage"
                        value={values.welcomeMessage}
                        onChange={(e) => setFieldValue("welcomeMessage", e.target.value)}
                        error={touched.welcomeMessage && Boolean(errors.welcomeMessage)}
                        helperText={touched.welcomeMessage && errors.welcomeMessage}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={i18n.t("settings.webchat.form.messagePlaceholder")}
                        name="messagePlaceholder"
                        value={values.messagePlaceholder}
                        onChange={(e) => setFieldValue("messagePlaceholder", e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={i18n.t("settings.webchat.form.companyName")}
                        name="companyName"
                        value={values.companyName}
                        onChange={(e) => setFieldValue("companyName", e.target.value)}
                        error={touched.companyName && Boolean(errors.companyName)}
                        helperText={touched.companyName && errors.companyName}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={i18n.t("settings.webchat.form.companyUrl")}
                        name="companyUrl"
                        value={values.companyUrl}
                        onChange={(e) => setFieldValue("companyUrl", e.target.value)}
                        error={touched.companyUrl && Boolean(errors.companyUrl)}
                        helperText={touched.companyUrl && errors.companyUrl}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1} className={classes.tabPanel}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{i18n.t("settings.webchat.form.queueId")}</InputLabel>
                    <Select
                      value={values.queueId || ""}
                      onChange={(e) => setFieldValue("queueId", e.target.value)}
                    >
                      <MenuItem value="">{i18n.t("settings.webchat.messages.noQueue")}</MenuItem>
                      {queues.map((queue) => (
                        <MenuItem key={queue.id} value={queue.id}>
                          {queue.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={i18n.t("settings.webchat.form.maxConcurrentChats")}
                    name="maxConcurrentChats"
                    value={values.maxConcurrentChats}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFieldValue("maxConcurrentChats", val === "" ? "" : parseInt(val) || 0);
                    }}
                    error={touched.maxConcurrentChats && Boolean(errors.maxConcurrentChats)}
                    helperText={touched.maxConcurrentChats && errors.maxConcurrentChats}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.autoAssign}
                        onChange={(e) => setFieldValue("autoAssign", e.target.checked)}
                        name="autoAssign"
                        color="primary"
                      />
                    }
                    label={i18n.t("settings.webchat.form.autoAssign")}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.respectBusinessHours}
                        onChange={(e) => setFieldValue("respectBusinessHours", e.target.checked)}
                        name="respectBusinessHours"
                        color="primary"
                      />
                    }
                    label={i18n.t("settings.webchat.form.respectBusinessHours")}
                  />
                </Grid>

                {values.respectBusinessHours && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={i18n.t("settings.webchat.form.offlineMessage")}
                      name="offlineMessage"
                      value={values.offlineMessage}
                      onChange={(e) => setFieldValue("offlineMessage", e.target.value)}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.enableAssistant}
                        onChange={(e) => setFieldValue("enableAssistant", e.target.checked)}
                        name="enableAssistant"
                        color="primary"
                      />
                    }
                    label={i18n.t("settings.webchat.form.enableAssistant")}
                  />
                </Grid>

                {values.enableAssistant && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{i18n.t("settings.webchat.form.assistantId")}</InputLabel>
                      <Select
                        value={values.assistantId || ""}
                        onChange={(e) => setFieldValue("assistantId", e.target.value)}
                        disabled={loadingAssistants}
                      >
                        <MenuItem value="">
                          {loadingAssistants
                            ? i18n.t("settings.webchat.messages.loadingAssistants")
                            : i18n.t("settings.webchat.messages.selectAssistant")
                          }
                        </MenuItem>
                        {assistants
                          .filter(a => a.active)
                          .map((assistant) => (
                            <MenuItem key={assistant.id} value={assistant.id}>
                              {assistant.name} ({assistant.model})
                            </MenuItem>
                          ))
                        }
                      </Select>
                      {assistants.filter(a => a.active).length === 0 && !loadingAssistants && (
                        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                          {i18n.t("settings.webchat.help.noAssistants")}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={2} className={classes.tabPanel}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.allowFileUpload}
                        onChange={(e) => setFieldValue("allowFileUpload", e.target.checked)}
                        name="allowFileUpload"
                        color="primary"
                      />
                    }
                    label={i18n.t("settings.webchat.form.allowFileUpload")}
                  />
                </Grid>

                {values.allowFileUpload && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={i18n.t("settings.webchat.form.allowedFileTypes")}
                        name="allowedFileTypes"
                        value={values.allowedFileTypes}
                        onChange={(e) => setFieldValue("allowedFileTypes", e.target.value)}
                        helperText={i18n.t("settings.webchat.help.allowedFileTypes")}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={i18n.t("settings.webchat.form.maxFileSize")}
                        name="maxFileSize"
                        value={values.maxFileSize}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFieldValue("maxFileSize", val === "" ? "" : parseInt(val) || 0);
                        }}
                        error={touched.maxFileSize && Boolean(errors.maxFileSize)}
                        helperText={touched.maxFileSize && errors.maxFileSize}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Typography variant="h6" style={{ marginBottom: 16 }}>
                    {i18n.t("settings.webchat.requiredFields")}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.requireName}
                        onChange={(e) => setFieldValue("requireName", e.target.checked)}
                        name="requireName"
                        color="primary"
                      />
                    }
                    label={i18n.t("settings.webchat.form.requireName")}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.requireEmail}
                        onChange={(e) => setFieldValue("requireEmail", e.target.checked)}
                        name="requireEmail"
                        color="primary"
                      />
                    }
                    label={i18n.t("settings.webchat.form.requireEmail")}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.requirePhone}
                        onChange={(e) => setFieldValue("requirePhone", e.target.checked)}
                        name="requirePhone"
                        color="primary"
                      />
                    }
                    label={i18n.t("settings.webchat.form.requirePhone")}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={3} className={classes.tabPanel}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    label={i18n.t("settings.webchat.form.customCSS")}
                    name="customCSS"
                    value={values.customCSS || ""}
                    onChange={(e) => setFieldValue("customCSS", e.target.value)}
                    placeholder={i18n.t("settings.webchat.placeholders.customCSS")}
                    helperText={i18n.t("settings.webchat.help.customCSS")}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={4} className={classes.tabPanel}>
              <Typography variant="h6" gutterBottom>
                {i18n.t("settings.webchat.code.title")}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {i18n.t("settings.webchat.code.description")}
              </Typography>
              <Paper className={classes.codeContainer}>
                <pre>{generateEmbedCode()}</pre>
              </Paper>
              <Button
                variant="outlined"
                color="primary"
                style={{ marginTop: 16 }}
                onClick={() => {
                  navigator.clipboard.writeText(generateEmbedCode())
                    .then(() => toastUtils.success(i18n.t("settings.webchat.messages.codeCopied"), "Código Copiado"))
                    .catch(() => toastUtils.success("Código copiado (fallback)", "Código Copiado"));
                }}
              >
                {i18n.t("settings.webchat.code.copyButton")}
              </Button>

              <Typography variant="h6" gutterBottom style={{ marginTop: 32 }}>
                Como Instalar no seu Site
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold", marginTop: 16 }}>
                🟢 Em um site WordPress
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                1. Instale e ative o plugin gratuito <b>"WP Headers and Footers"</b> no seu painel WP.<br />
                2. Vá em Configurações &gt; WP Headers and Footers.<br />
                3. Cole o código acima na caixa <b>"Scripts in Footer"</b> e clique em Salvar.<br />
                4. Pronto! O balão do chat aparecerá em todas as páginas do site.
              </Typography>

              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold", marginTop: 16 }}>
                🟢 Em uma página HTML (Landing Page ou Site Comum)
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                1. Abra o arquivo HTML da sua página usando um editor (Bloco de Notas, VS Code).<br />
                2. Role o código até encontrar a tag final <code>&lt;/body&gt;</code>.<br />
                3. Cole o código de integração imediatamente <b>acima</b> dessa tag.<br />
                4. Salve e publique o arquivo no seu servidor.
              </Typography>
            </TabPanel>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                  style={{ float: "right" }}
                >
                  {i18n.t("settings.webchat.buttons.save")}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default WebChatSettings;